import { authFetchPOST, authFetchGET } from '../utils/auth-fetch';
import { WeatherModel } from '../models/weather-model';
import { VoyageRoute, RoutingJobOptions, VoyageRoutePointFeature, NextWaypointPayload, NextWaypointResult, SubscriptionInfo, WeatherLimits, RouteGeometry, WeatherSummary } from '../models/voyage-routing-model';

const env = (<any>window)["appenv"].services.routeoptimiserapi;

interface InputRoute
{
    forecast: string;
    etd: string;
    eta: string;
    draughtFore: number;
    draughtAft: number;
    reeferCount: number;
    weatherLimits: WeatherLimits;
    routeGeometry: RouteGeometry;
    scheduleId: string;
    gmt?: number;
}

interface CalculatedRoute
{
    forecast: string;
    forecastHasLowResData: boolean;
    forecastHasHighResData: boolean;
    subscriptions: { id: string; importedAt: string; }[];
    etd: string;
    eta: string;
    reeferCount: number;
    reeferConsumption: number;
    draughtFore: number;
    draughtAft: number;
	gmt?: number;
	routeGeometry: RouteGeometry;
	weatherLimits: WeatherLimits;
    weatherSummary: {[index:string]: WeatherSummary};
    totals: {
        fuelConsumption: number;
        fuelConsumptionLower?: number;
        fuelConsumptionUpper?: number;
        distance: number;
        speedOverGround: number;
    };
    hydraRequestId: string;
    hydraErrors: null|any[];
}

interface RouteOptimiserResult
{
    id: number;
    createdAt: string;
    updatedAt: string;
    status: string;
    statusDetails?: string;
    errorCode?: number;
    errorDetails?: {[index:string]:any};
    inputRoute: InputRoute;
    calculatedRoute?: CalculatedRoute;
}

export interface RouteOptimiserJobResult extends RouteOptimiserResult
{
    route?: VoyageRoute
}

export const RouteOptimizerService = new class extends EventTarget
{
    async getNextWaypointIndex(payload: NextWaypointPayload) : Promise<NextWaypointResult>{
        const result = await authFetchPOST(env.scope, `${env.baseurl}/v1/calculate-next-waypoint`, payload);

        return result;
    }

    async postCalculationJob(route: VoyageRoute, options: RoutingJobOptions): Promise<number>
    {
        const draughtFore = options.draftFore!;
        const draughtAft = options.draftAft!;
        const reeferCount = options.numberOfReefers!;
        const forecast = options.forecastVersion || WeatherModel!.latestForecastVersion;
        const etd = options.etd || Date.now();
        const routeGeometry = JSON.parse(JSON.stringify(route.route_geometry));
        const weatherLimits = options.weatherLimits;

        const payload = {
            inputRoute: {
                forecast,
                etd: new Date(etd).toISOString(),
                eta: new Date(options.eta!).toISOString(),
                draughtFore,
                draughtAft,
                reeferCount,
                weatherLimits,
                routeGeometry,
                scheduleId: options.planScheduleId,
                gmt: options.gmt
            }
        };

        const postResult = await authFetchPOST(env.scope, `${env.baseurl}/v1/calculations`, payload);
        if (!postResult || !postResult.id)
        {
            console.warn('Unknown response from route optimiser API:', postResult);
            throw new Error("Unknown response from route optimiser API");
        }

        return <number>postResult.id;
    }

    async waitForJob(jobId: number, options: RoutingJobOptions): Promise<RouteOptimiserJobResult>
    {
        let status = "pending";
        let result: RouteOptimiserJobResult|null = null;
        while (status === "pending")
        {
            result = <RouteOptimiserResult>await authFetchGET(env.scope, `${env.baseurl}/v1/calculations/${jobId}`);
            status = result.status ? result.status : "failed";
            if (status === "pending")
            {
                await new Promise(resolve => setTimeout(resolve, 1000)); // Wait one second between polls
            }
        }

        if (status !== "complete")
        {
            return result!;
        }

        // Convert to VoyageRoute format
        const endpoint = <VoyageRoutePointFeature>result!.calculatedRoute!.routeGeometry[result!.calculatedRoute!.routeGeometry.length-1];
        const duration = new Date(endpoint.properties.timestamp!).getTime() - new Date(result!.calculatedRoute!.etd).getTime();

        const subscriptions: SubscriptionInfo[] = [];
        if (result!.calculatedRoute!.subscriptions)
        {
            for (const subInfo of result!.calculatedRoute!.subscriptions)
            {
                subscriptions.push({
                    forecast: result!.calculatedRoute!.forecast,
                    id: subInfo.id,
                    importedAt: subInfo.importedAt
                });
            }
        }

        const route: VoyageRoute = {
            route_geometry: result!.calculatedRoute!.routeGeometry,
            route_info: {
                eta: result!.calculatedRoute!.eta,
                etd: result!.calculatedRoute!.etd,
                speed_over_ground: result!.calculatedRoute!.totals.speedOverGround,
                co2: -1, rpm: -1, // Not available
                totals: {
                    eca: { time: 0, fuel_cost: 0, vessel_cost: 0, distance: 0, fuel: 0 },
                    non_eca: {
                        time: duration/60*60*1000, // convert to hours
                        fuel_cost: 0, vessel_cost: 0,
                        distance: result!.calculatedRoute!.totals.distance,
                        fuel: result!.calculatedRoute!.totals.fuelConsumption
                    }
                },
                weather: {
                    forecast_versions: [ result!.calculatedRoute!.forecast ],
                    subscriptions: subscriptions,
                    summary: result!.calculatedRoute!.weatherSummary,
                    hasLowResData: result!.calculatedRoute!.forecastHasLowResData,
                    hasHighResData: result!.calculatedRoute!.forecastHasHighResData
                },
                calculationInput: {
                    forecast_versions: [ result!.calculatedRoute!.forecast ],
                    draftFore: result!.calculatedRoute!.draughtFore,
                    draftAft: result!.calculatedRoute!.draughtAft,
                    gmt: result!.calculatedRoute!.gmt,
                    etd: result!.calculatedRoute!.etd,
                    eta: result!.calculatedRoute!.eta,
                    weatherLimits: result!.calculatedRoute!.weatherLimits
                },
                hydraRequestId: result!.calculatedRoute!.hydraRequestId,
                hydraErrors: result!.calculatedRoute!.hydraErrors || []
            }
        }

        result!.route = route;

        return result!;
    }
};
